/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from 'react-router-dom';
import { Grid, Box, Typography } from '@mui/material';
import validate from 'validate.js';
import moment from 'moment';
import * as selectorsJornada from '../../../store/jornada/reducer';
import * as actionsJornada from '../../../store/jornada/actions';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as Role from '../../../constants/role';
import { JORNADA_ITEM_TIPOS } from '../../../constants/jornadaItemTipo';
import {
  DialogDefaultV2,
  InputV2,
  InputFormatV2,
  UsuarioSelectV2,
  OptionSelectV2,
  DatePickerV2,
  WidgetV2,
} from '../../../components/v2';
import { convertTimeToMinutes, isNullOrUndefined, isValidTime, formatToTime } from 'utils';
import {
  buildItensFromTimes,
  computeJornadaTotalMinutes,
  canEditJornada,
  geoPointsFromItens,
  timesFromItens,
  itensByTipo,
} from '../../../helpers/jornadaHelpers';
import JornadaGeoLine from '../../../helpers/JornadaGeoLine';
import JornadaPontosMap from '../../../helpers/JornadaPontosMap';
import {
  canTransitionJornadaValidado,
  canValidateJornada,
  getJornadaValidadoDisplay,
  JORNADA_SEM_HORA_EXTRA,
  JORNADA_SEM_HORA_EXTRA_LABEL,
} from '../../../helpers/jornadaValidado';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import InvalidarDialog from './InvalidarDialog';

const defaultSchema = {
  data: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  ocorrenciaId: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  inspetor: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
  inicioJornada: {
    presence: { allowEmpty: false, message: 'é obrigatório' },
  },
};

const validateInvalidTimes = (values) => {
  let errors = {};

  if (!isNullOrUndefined(values.inicioJornada) && !isValidTime(values.inicioJornada)) {
    errors = { ...errors, inicioJornada: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.inicioIntervalo) && !isValidTime(values.inicioIntervalo)) {
    errors = { ...errors, inicioIntervalo: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.finalIntervalo) && !isValidTime(values.finalIntervalo)) {
    errors = { ...errors, finalIntervalo: ['Horário inválido'] };
  }
  if (!isNullOrUndefined(values.finalJornada) && !isValidTime(values.finalJornada)) {
    errors = { ...errors, finalJornada: ['Horário inválido'] };
  }

  return errors;
};

const hasError = (values, horaExtraDisabled) => {
  let schema = defaultSchema;
  if (!horaExtraDisabled) {
    schema = {
      ...schema,
      horaExtraAutorizadoPor: { presence: { allowEmpty: false, message: 'é obrigatório' } },
      horaExtraJustificativa: { presence: { allowEmpty: false, message: 'é obrigatório' } },
    };
  }

  return {
    ...validate(values, schema),
    ...validateInvalidTimes(values),
  };
};

const emptyValues = (adminPermission, authData) => ({
  data: moment().valueOf(),
  tipoControle: 'FOLHA_PONTO',
  inspetor: !adminPermission ? authData?.id : null,
  itens: [],
});

const Formulario = ({
  open,
  id,
  onClose,
  loadJornada,
  jornada,
  saveJornada,
  validateJornada,
  authData,
  adminPermission,
}) => {
  const [readOnly, setReadOnly] = useState(!adminPermission);
  const [errors, setErrors] = useState({});
  const [horaExtraDisabled, setHoraExtraDisabled] = useState(true);
  const [openMapa, setOpenMapa] = useState(false);
  const [openInvalidar, setOpenInvalidar] = useState(false);
  const [values, setValues] = useState(() => emptyValues(adminPermission, authData));

  const byTipo = useMemo(() => itensByTipo(values.itens), [values.itens]);
  const pontos = useMemo(() => geoPointsFromItens(values.itens), [values.itens]);
  const showHoraExtra = !horaExtraDisabled
    || Boolean(values.horaExtraAutorizadoPor)
    || Boolean(values.horaExtraJustificativa);
  const canValidateUser = showHoraExtra
    && canValidateJornada(values, authData?.id, adminPermission);
  const canApprove = canValidateUser
    && canTransitionJornadaValidado(values.validado, 'VALIDADO');
  const canInvalidar = canValidateUser
    && canTransitionJornadaValidado(values.validado, 'INVALIDADO');

  const validateHoraExtra = (data, jornadaTotalMinutes) => {
    if (data) {
      const day = (new Date(data)).getDay();
      if (day === 0) {
        setHoraExtraDisabled(false);
        return;
      }
    }

    if (!jornadaTotalMinutes || !data) {
      setHoraExtraDisabled(true);
      return;
    }

    const day = (new Date(data)).getDay();
    let nextHoraExtraDisabled = true;
    if (day === 0) {
      nextHoraExtraDisabled = false;
    } else if (day === 6) {
      nextHoraExtraDisabled = jornadaTotalMinutes <= 4 * 60;
    } else {
      nextHoraExtraDisabled = jornadaTotalMinutes <= 8 * 60;
    }

    setHoraExtraDisabled(nextHoraExtraDisabled);
  };

  useEffect(() => {
    if (id) loadJornada(id);
  }, [id, loadJornada]);

  useEffect(() => {
    if (id && jornada && id === jornada.id) {
      const times = timesFromItens(jornada.itens);
      const next = {
        ...jornada,
        ...times,
        data: moment(parseInt(jornada.data, 10)).valueOf(),
        itens: jornada.itens || [],
      };
      const totalMinutes = computeJornadaTotalMinutes(times, convertTimeToMinutes)
        || jornada.jornadaTotalMinutes;
      setValues({
        ...next,
        jornadaTotalMinutes: totalMinutes,
        jornadaTotal: totalMinutes ? formatToTime(totalMinutes) : (jornada.jornadaTotal || null),
      });
      setReadOnly(!canEditJornada(jornada, authData?.id, adminPermission));
      validateHoraExtra(
        moment(parseInt(jornada.data, 10)).valueOf(),
        totalMinutes,
      );
    } else if (!id) {
      setValues(emptyValues(adminPermission, authData));
      setReadOnly(false);
      setHoraExtraDisabled(true);
    }
  }, [jornada, id, adminPermission, authData?.id]);

  const onSave = () => {
    const erros = hasError(values, horaExtraDisabled);
    if (erros != null && Object.keys(erros).length > 0) {
      setErrors(erros);
      return;
    }

    const itens = buildItensFromTimes(values, values.itens);
    saveJornada({
      id: values.id,
      data: values.data,
      tipoControle: 'FOLHA_PONTO',
      inspetor: values.inspetor,
      inspetorAuxiliar: values.inspetorAuxiliar || null,
      ocorrenciaId: values.ocorrenciaId,
      jornadaTotal: values.jornadaTotal || null,
      horaExtraJustificativa: values.horaExtraJustificativa || null,
      horaExtraAutorizadoPor: values.horaExtraAutorizadoPor || null,
      itens,
    }, () => onCloseLocal(true));
  };

  const onCloseLocal = (isSave) => {
    setErrors({});
    setValues(emptyValues(adminPermission, authData));
    setReadOnly(false);
    setHoraExtraDisabled(true);
    setOpenMapa(false);
    setOpenInvalidar(false);
    onClose(isSave);
  };

  const applyValidadoResponse = (data) => {
    if (!data) return;
    setValues((prev) => ({
      ...prev,
      validado: data.validado ?? null,
      motivoInvalidacao: data.motivoInvalidacao ?? null,
    }));
  };

  const handleAprovar = () => {
    if (!values.id) return;
    validateJornada(values.id, { validado: 'VALIDADO' }, (ok, data) => {
      if (ok) applyValidadoResponse(data);
    });
  };

  const handleInvalidar = (motivoInvalidacao) => {
    if (!values.id) return;
    validateJornada(values.id, { validado: 'INVALIDADO', motivoInvalidacao }, (ok, data) => {
      if (ok) {
        applyValidadoResponse(data);
        setOpenInvalidar(false);
      }
    });
  };

  const onChangeTime = (e, field) => {
    if (readOnly) return;
    const value = e.target.value;
    const nextTimes = {
      inicioJornada: field === 'inicioJornada' ? value : values.inicioJornada,
      inicioIntervalo: field === 'inicioIntervalo' ? value : values.inicioIntervalo,
      finalIntervalo: field === 'finalIntervalo' ? value : values.finalIntervalo,
      finalJornada: field === 'finalJornada' ? value : values.finalJornada,
    };
    const jornadaTotal = computeJornadaTotalMinutes(nextTimes, convertTimeToMinutes);
    const nextValues = {
      ...values,
      [field]: value,
      jornadaTotal: jornadaTotal ? formatToTime(jornadaTotal) : null,
      jornadaTotalMinutes: jornadaTotal,
    };
    setValues(nextValues);
    validateHoraExtra(nextValues.data, jornadaTotal);
  };

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={() => onCloseLocal(false)}
      handlerConfirm={onSave}
      title="Folha de Ponto"
      confirmMessage="Salvar"
      maxWidth="md"
      fullWidth
      readOnly={readOnly}
      headerAction={[
        {
          title: 'Aprovar',
          show: canApprove,
          onClick: handleAprovar,
        },
        {
          title: 'Invalidar',
          show: canInvalidar,
          onClick: () => setOpenInvalidar(true),
        },
      ]}
    >
      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <DatePickerV2
            label="Data"
            onChange={(data) => {
              setValues({ ...values, data });
              validateHoraExtra(data, values.jornadaTotalMinutes);
            }}
            value={values.data}
            isRequired
            error={!!(errors && errors.data)}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <OptionSelectV2
            label="Ocorrência"
            codigo="PONTO_OCORRENCIA"
            isRequired
            error={!!(errors && errors.ocorrenciaId)}
            onSelect={(ocorrenciaId) => setValues({ ...values, ocorrenciaId })}
            value={values.ocorrenciaId}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <UsuarioSelectV2
            label="Usuário"
            onSelect={(inspetor) => setValues({ ...values, inspetor })}
            isRequired
            error={!!(errors && errors.inspetor)}
            value={values.inspetor}
            disabled={readOnly || !adminPermission || Boolean(id)}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <UsuarioSelectV2
            clearable
            label="Parceiro"
            onSelect={(inspetorAuxiliar) => setValues({ ...values, inspetorAuxiliar })}
            grupos={[Role.ADMIN, Role.INSPETOR]}
            value={values.inspetorAuxiliar}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Validação hora extra
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
            {renderStatus(
              showHoraExtra
                ? getJornadaValidadoDisplay(values.validado, true)
                : { value: JORNADA_SEM_HORA_EXTRA, label: JORNADA_SEM_HORA_EXTRA_LABEL },
            )}
          </Box>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <WidgetV2
            title="Horários"
            collapse={false}
            initialExpanded
            insideTab
            sxContent={{ p: 1.5 }}
            actions={[{
              title: 'Mapa',
              show: pontos.length > 0,
              onClick: () => setOpenMapa(true),
            }]}
          >
            <Grid container spacing={1}>
              {JORNADA_ITEM_TIPOS.map(({ tipo, timeField, label }) => (
                <Grid key={tipo} size={{ xs: 12, sm: 6 }}>
                  <InputFormatV2
                    label={label}
                    value={values[timeField]}
                    onChange={(e) => onChangeTime(e, timeField)}
                    format="##:##"
                    placeholder="HH:MM"
                    disabled={readOnly}
                    isRequired={timeField === 'inicioJornada'}
                    error={!!(errors && errors[timeField])}
                    helperText={errors && errors[timeField] ? errors[timeField] : null}
                  />
                  <JornadaGeoLine item={byTipo[tipo]} />
                </Grid>
              ))}
              <Grid size={{ xs: 12, sm: 6 }} sx={{ pb: 1.5 }}>
                <InputV2 label="Hora Total Trabalhada" disabled value={values.jornadaTotal || ''} />
              </Grid>
            </Grid>
          </WidgetV2>
        </Grid>
        {showHoraExtra ? (
          <>
            <Grid size={{ xs: 12, sm: 4 }}>
              <UsuarioSelectV2
                label="Hora Extra Autorizado Por"
                onSelect={(horaExtraAutorizadoPor) => setValues({
                  ...values,
                  horaExtraAutorizadoPor,
                })}
                grupos={[Role.ADMIN, Role.SUPER_ADMIN]}
                value={values.horaExtraAutorizadoPor}
                disabled={horaExtraDisabled || readOnly}
                isRequired={!horaExtraDisabled}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <InputV2
                label="Justificativa Hora Extra"
                name="horaExtraJustificativa"
                onChange={(event) => setValues({
                  ...values,
                  horaExtraJustificativa: event.target.value,
                })}
                value={values.horaExtraJustificativa || ''}
                rows={3}
                multiline
                disabled={horaExtraDisabled || readOnly}
                isRequired={!horaExtraDisabled}
              />
            </Grid>
            {values.validado === 'INVALIDADO' && values.motivoInvalidacao ? (
              <Grid size={{ xs: 12 }}>
                <InputV2
                  label="Motivo invalidação"
                  name="motivoInvalidacao"
                  value={values.motivoInvalidacao}
                  rows={2}
                  multiline
                  disabled
                />
              </Grid>
            ) : null}
          </>
        ) : null}
      </Grid>
      <JornadaPontosMap
        open={openMapa}
        onClose={() => setOpenMapa(false)}
        itens={values.itens}
      />
      <InvalidarDialog
        open={openInvalidar}
        onClose={() => setOpenInvalidar(false)}
        onConfirm={handleInvalidar}
      />
    </DialogDefaultV2>
  );
};

Formulario.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  loadJornada: PropTypes.func.isRequired,
  jornada: PropTypes.object,
  saveJornada: PropTypes.func.isRequired,
  validateJornada: PropTypes.func.isRequired,
  authData: PropTypes.object.isRequired,
  adminPermission: PropTypes.bool.isRequired,
};

const mapStateToProps = (state) => ({
  jornada: selectorsJornada.getSelectedJornada(state),
  authData: selectorsAuth.getAuth(state),
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...actionsJornada,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Formulario));
